import useAuthStore from '@/stores/useAuthStore'
import { useMemo } from 'react'
import DraggableLayout, { WidgetConfig } from '../shared/DraggableLayout'

// Import shared components
import Paper from '@/components/Paper'
import Business from '@/modules/Dashboard/components/shared/Business'
import TargetRevenue from '@/modules/Dashboard/components/shared/TargetRevenue'
import WorkDays from '@/modules/Dashboard/components/shared/WorkDays'
import MyWorkdays from '@/modules/Dashboard/components/shared/MyWorkdays'
import dayjs from 'dayjs'
import Events from '../../shared/Events'
import Notifications from '../../shared/Notifications'
import QuoteOfTheDay from '../../shared/QuoteOfTheDay'

// Import Sales specific widgets (Executive can see sales metrics)
import CancellationRateWidget from '../AccountDashboard/widgets/CancellationRateWidget'
import LateEmployeesWidget from '../HRDashboard/widgets/LateEmployeesWidget'
import ConversionFunnelWidget from '../SalesDashboard/widgets/ConversionFunnelWidget'
import DailyInputWidget from '../SalesDashboard/widgets/DailyInputWidget'
import LeadsStatistics from '../../shared/LeadsStatistics'
import ServiceTrendWidget from '../AccountDashboard/widgets/ServiceTrendWidget'
import InvoiceWaitingWidget from '../AccountDashboard/widgets/InvoiceWaitingWidget'
import RevenueAllocationWidget from '../FinanceDashboard/widgets/RevenueAllocationWidget'
import RecentlyPaidInvoicesWidget from '../FinanceDashboard/widgets/RecentlyPaidInvoicesWidget'
import InternalInformation from '../../shared/InternalInformation'

const ExecutiveDashboard = () => {
    const employee = useAuthStore((state) => state.employee)

    // Storage key unique per employee
    const storageKey = useMemo(
        () =>
            `dashboard_layout_executive_${employee?.employee_id || 'default'}`,
        [employee?.employee_id]
    )

    // Widget configurations with colSpan (3, 4, 6, 8, or 12)
    const widgets: WidgetConfig[] = useMemo(
        () => [
            {
                id: 'quote_of_the_day',
                component: (
                    <Paper className="h-full">
                        <QuoteOfTheDay />
                    </Paper>
                ),
                colSpan: 12,
            },
            {
                id: 'revenue',
                component: (
                    <Paper className="h-full">
                        <TargetRevenue
                            gte={`${dayjs().startOf('year').format('YYYY-MM-DD')}`}
                            lte={`${dayjs().endOf('year').format('YYYY-MM-DD')}`}
                        />
                    </Paper>
                ),
                colSpan: 6,
            },
            {
                id: 'late_employees',
                component: (
                    <Paper className="h-full">
                        <LateEmployeesWidget />
                    </Paper>
                ),
                colSpan: 3,
            },
            {
                id: 'events',
                component: (
                    <Paper className="h-full">
                        <Events />
                    </Paper>
                ),
                colSpan: 3,
            },
            {
                id: 'my_workdays',
                component: (
                    <Paper className="h-full">
                        <MyWorkdays />
                    </Paper>
                ),
                colSpan: 3,
            },
            {
                id: 'daily-input',
                component: <DailyInputWidget />,
                colSpan: 6,
            },
            {
                id: 'accounting-leads-statistics',
                component: <LeadsStatistics />,
                colSpan: 3,
            },
            {
                id: 'conversion-funnel',
                component: <ConversionFunnelWidget />,
                colSpan: 3,
            },
            {
                id: 'invoice-waiting',
                component: <InvoiceWaitingWidget />,
                colSpan: 4,
            },
            {
                id: 'recently-paid',
                component: <RecentlyPaidInvoicesWidget />,
                colSpan: 4,
            },
            {
                id: 'invoice-revenue',
                component: <RevenueAllocationWidget />,
                colSpan: 4,
            },
            {
                id: 'service-trend',
                component: <ServiceTrendWidget />,
                colSpan: 6,
            },
            {
                id: 'cancellation-rate',
                component: <CancellationRateWidget />,
                colSpan: 6,
            },
            {
                id: 'notification',
                component: (
                    <Paper className="h-full">
                        <Notifications />
                    </Paper>
                ),
                colSpan: 6,
            },
            {
                id: 'business',
                component: (
                    <Paper className="h-full">
                        <Business />
                    </Paper>
                ),
                colSpan: 3,
            },
            {
                id: 'workday',
                component: (
                    <Paper className="h-full">
                        <WorkDays />
                    </Paper>
                ),
                colSpan: 3,
            },
            {
                id: 'internal-information',
                component: (
                    <Paper className="h-full">
                        <InternalInformation />
                    </Paper>
                ),
                colSpan: 9,
            },
        ],
        []
    )

    return (
        <DraggableLayout
            widgets={widgets}
            storageKey={storageKey}
            department="BOARD OF DIRECTORS"
        />
    )
}

export default ExecutiveDashboard
